import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { isEqual, isEmpty } from 'lodash';
import { formatStat } from 'utils/misc';
import moment from 'moment';
import { DATE_FORMAT_VERBOSE } from 'constants/config';
import TableSortLabel from 'components/tables/tableSortLabel';

import {
  getReportCampaigns,
} from 'redux/actions/reportActions';
import Table from 'components/tables';
import TableHead from 'components/tables/tableHead';
import TableBody from 'components/tables/tableBody';
import TableCell from 'components/tables/tableCell';
import TableRow from 'components/tables/tableRow';
import NoResults from 'components/NoResults';
import Pagination from 'components/pagination/Pagination';
import { VIEW_CAMPAIGNS_TAB_ANALYTICS } from 'constants/analytics';
import { getTimezoneAndUTCOffset } from 'utils/report';
import {
  campaignDetailUrl,
} from 'constants/routing';
import { analyticsTrack } from '../../../utils/analytics';
import { formatPotentialReach } from '../../../utils/report';


const renderCampaigns = campaign => (
  <TableRow>
    <TableCell>
      <Link to={campaignDetailUrl(campaign.id)}>
        {campaign.name}
      </Link>
    </TableCell>
    <TableCell>{campaign.launch_date
      ? moment(campaign.launch_date).format(DATE_FORMAT_VERBOSE)
      : ''}
    </TableCell>
    <TableCell>{campaign.sent}</TableCell>
    <TableCell>{campaign.delivered}</TableCell>
    <TableCell>{formatStat(campaign.people_contacted, campaign.opened)}</TableCell>
    <TableCell>{formatStat(campaign.people_contacted, campaign.replied)}</TableCell>
    <TableCell>{formatPotentialReach(campaign.potential_reach)}</TableCell>
    <TableCell>{formatStat(campaign.people_contacted, campaign.interested)}</TableCell>
    <TableCell>{formatStat(campaign.people_contacted, campaign.expanded_interest)}</TableCell>
    <TableCell>{formatStat(campaign.people_contacted, campaign.not_interested)}</TableCell>
  </TableRow>
);


class CampaignsTab extends Component {
  static propTypes = {
    report: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      reportParams: PropTypes.shape({}).isRequired,
      campaigns: PropTypes.shape({
        results: PropTypes.shape({
          length: PropTypes.number.isRequired,
        }),
      }),
    }).isRequired,
    getReportCampaigns: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      ordering: null,
      orderState: 'asc',
    };
  }

  componentDidMount() {
    analyticsTrack(VIEW_CAMPAIGNS_TAB_ANALYTICS, {}, true);
    if (!isEmpty(this.props.report.reportParams)) {
      this.props.getReportCampaigns({
        ...this.props.report.reportParams,
        ...getTimezoneAndUTCOffset(),
        ordering: this.calculateOrdering(this.state.ordering, this.state.orderState),
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { report: { reportParams } } = this.props;
    if (!isEqual(reportParams, prevProps.report.reportParams)) {
      this.props.getReportCampaigns({
        ...reportParams,
        ...getTimezoneAndUTCOffset(),
        ordering: this.calculateOrdering(this.state.ordering, this.state.orderState),
      });
    }
  }

  handleOrdering = (ordering) => {
    const orderState = this.state.orderState === 'asc' ? 'desc' : 'asc';
    this.setState({ orderState, ordering });
    this.props.getReportCampaigns({
      ...this.props.report.reportParams,
      ...getTimezoneAndUTCOffset(),
      ordering: this.calculateOrdering(ordering, orderState),
    });
  };

  calculateOrdering = (property, sorting) => {
    if (sorting === 'desc' && property) {
      return `-${property}`;
    }
    return property;
  }

  render() {
    const { campaigns } = this.props.report;
    const { orderState } = this.state;

    return (
      <div className="mb-4 overflow-auto">
        {(!this.props.report.loading && campaigns.results.length >= 1) &&
        <Table className="volt-table" noRadius>
          <TableHead>
            <TableRow>
              <TableCell onClick={() => this.handleOrdering('name')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="name"
                >
                  Campaign
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => this.handleOrdering('launch_date')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="launch_date"
                >
                  Launch Date
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => this.handleOrdering('sent')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="sent"
                >
                  Sent
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => this.handleOrdering('delivered')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="delivered"
                >
                  Delivered
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => this.handleOrdering('opened')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="opened"
                >
                  Opened
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => this.handleOrdering('replied')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="replied"
                >
                  Replied
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => this.handleOrdering('potential_reach')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="potential_reach"
                >
                  Potential Reach
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => this.handleOrdering('interested')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="interested"
                >
                  Interested
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => this.handleOrdering('expanded_interest')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="expanded_interest"
                >
                  Expanded Interest
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => this.handleOrdering('not_interested')}>
                <TableSortLabel
                  order={orderState}
                  orderBy={this.state.ordering}
                  property="not_interested"
                >
                  Not Interested
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {campaigns.results.length > 0 &&
              campaigns.results.map(campaign => renderCampaigns(campaign))
            }
          </TableBody>
        </Table>
        }

        <NoResults
          loading={campaigns.loading}
          results={campaigns.results}
        />
        <Pagination
          items={campaigns}
          onChangePage={this.props.getReportCampaigns}
          params={this.props.report.reportParams}
        />
      </div>
    );
  }
}


export default compose(connect(
  state => ({
    report: state.report,
  }),
  { getReportCampaigns },
), withNamespaces('common'))(CampaignsTab);
